import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import CampaignsV2API from '../../api/campaignsV2';
import { INBOX_TYPES } from 'dashboard/helper/inbox';

const SET_CAMPAIGNS_V2 = 'SET_CAMPAIGNS_V2';
const ADD_CAMPAIGN_V2 = 'ADD_CAMPAIGN_V2';
const EDIT_CAMPAIGN_V2 = 'EDIT_CAMPAIGN_V2';
const DELETE_CAMPAIGN_V2 = 'DELETE_CAMPAIGN_V2';
const SET_CAMPAIGNS_V2_UI_FLAG = 'SET_CAMPAIGNS_V2_UI_FLAG';
const SET_CAMPAIGN_V2_ANALYTICS = 'SET_CAMPAIGN_V2_ANALYTICS';
const SET_CAMPAIGN_V2_EXECUTIONS = 'SET_CAMPAIGN_V2_EXECUTIONS';
const SET_CAMPAIGN_V2_EXECUTION_MESSAGES = 'SET_CAMPAIGN_V2_EXECUTION_MESSAGES';

export const state = {
  records: [],
  analytics: {},
  executions: {},
  executionMessages: {},
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    isTriggering: false,
    isFetchingAnalytics: false,
    isExporting: false,
    isFetchingExecutions: false,
    isFetchingMessages: false,
  },
};

export const getters = {
  getUIFlags(_state) {
    return _state.uiFlags;
  },
  getAllCampaigns(_state) {
    return _state.records;
  },
  getWhatsAppCampaigns(_state) {
    return _state.records
      .filter(
        r =>
          r.inbox && r.inbox.channel_type === INBOX_TYPES.WHATSAPP
      )
      .sort((a, b) => a.id - b.id);
  },
  getApiCampaigns(_state) {
    return _state.records
      .filter(
        r =>
          r.inbox && r.inbox.channel_type === INBOX_TYPES.API
      )
      .sort((a, b) => a.id - b.id);
  },
  getAnalytics: _state => campaignId => {
    return _state.analytics[campaignId] || {};
  },
  getExecutions: _state => campaignId => {
    return _state.executions[campaignId] || [];
  },
  getExecutionMessages: _state => executionId => {
    return _state.executionMessages[executionId] || [];
  },
};

export const actions = {
  get: async ({ commit }) => {
    commit(SET_CAMPAIGNS_V2_UI_FLAG, { isFetching: true });
    try {
      const response = await CampaignsV2API.get();
      commit(SET_CAMPAIGNS_V2, response.data);
    } catch (error) {
      // Ignore error
    } finally {
      commit(SET_CAMPAIGNS_V2_UI_FLAG, { isFetching: false });
    }
  },

  create: async ({ commit }, campaignObj) => {
    commit(SET_CAMPAIGNS_V2_UI_FLAG, { isCreating: true });
    try {
      const response = await CampaignsV2API.create(campaignObj);
      commit(ADD_CAMPAIGN_V2, response.data);
      return response.data;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(SET_CAMPAIGNS_V2_UI_FLAG, { isCreating: false });
    }
  },

  update: async ({ commit }, { id, ...updateObj }) => {
    commit(SET_CAMPAIGNS_V2_UI_FLAG, { isUpdating: true });
    try {
      const response = await CampaignsV2API.update(id, updateObj);
      commit(EDIT_CAMPAIGN_V2, response.data);
      return response.data;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(SET_CAMPAIGNS_V2_UI_FLAG, { isUpdating: false });
    }
  },

  delete: async ({ commit }, id) => {
    commit(SET_CAMPAIGNS_V2_UI_FLAG, { isDeleting: true });
    try {
      await CampaignsV2API.delete(id);
      commit(DELETE_CAMPAIGN_V2, id);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(SET_CAMPAIGNS_V2_UI_FLAG, { isDeleting: false });
    }
  },

  trigger: async ({ commit }, campaignId) => {
    commit(SET_CAMPAIGNS_V2_UI_FLAG, { isTriggering: true });
    try {
      await CampaignsV2API.trigger(campaignId);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(SET_CAMPAIGNS_V2_UI_FLAG, { isTriggering: false });
    }
  },

  fetchAnalytics: async ({ commit }, campaignId) => {
    commit(SET_CAMPAIGNS_V2_UI_FLAG, { isFetchingAnalytics: true });
    try {
      const response = await CampaignsV2API.analytics(campaignId);
      commit(SET_CAMPAIGN_V2_ANALYTICS, {
        campaignId,
        data: response.data,
      });
    } catch (error) {
      // Ignore error
    } finally {
      commit(SET_CAMPAIGNS_V2_UI_FLAG, { isFetchingAnalytics: false });
    }
  },

  exportCampaign: async ({ commit }, campaignId) => {
    commit(SET_CAMPAIGNS_V2_UI_FLAG, { isExporting: true });
    try {
      await CampaignsV2API.exportCampaign(campaignId);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(SET_CAMPAIGNS_V2_UI_FLAG, { isExporting: false });
    }
  },

  uploadMedia: async (_, { campaignId, formData }) => {
    const response = await CampaignsV2API.uploadMedia(campaignId, formData);
    return response.data;
  },

  fetchExecutions: async ({ commit }, campaignId) => {
    commit(SET_CAMPAIGNS_V2_UI_FLAG, { isFetchingExecutions: true });
    try {
      const response = await CampaignsV2API.getExecutions(campaignId);
      commit(SET_CAMPAIGN_V2_EXECUTIONS, {
        campaignId,
        data: response.data,
      });
    } catch (error) {
      // Ignore error
    } finally {
      commit(SET_CAMPAIGNS_V2_UI_FLAG, { isFetchingExecutions: false });
    }
  },

  fetchExecutionMessages: async (
    { commit },
    { campaignId, executionId, page }
  ) => {
    commit(SET_CAMPAIGNS_V2_UI_FLAG, { isFetchingMessages: true });
    try {
      const response = await CampaignsV2API.getExecutionMessages(
        campaignId,
        executionId,
        { page }
      );
      commit(SET_CAMPAIGN_V2_EXECUTION_MESSAGES, {
        executionId,
        data: response.data,
      });
    } catch (error) {
      // Ignore error
    } finally {
      commit(SET_CAMPAIGNS_V2_UI_FLAG, { isFetchingMessages: false });
    }
  },
};

export const mutations = {
  [SET_CAMPAIGNS_V2_UI_FLAG](_state, data) {
    _state.uiFlags = { ..._state.uiFlags, ...data };
  },
  [SET_CAMPAIGNS_V2]: MutationHelpers.set,
  [ADD_CAMPAIGN_V2]: MutationHelpers.create,
  [EDIT_CAMPAIGN_V2]: MutationHelpers.update,
  [DELETE_CAMPAIGN_V2]: MutationHelpers.destroy,
  [SET_CAMPAIGN_V2_ANALYTICS](_state, { campaignId, data }) {
    _state.analytics = { ..._state.analytics, [campaignId]: data };
  },
  [SET_CAMPAIGN_V2_EXECUTIONS](_state, { campaignId, data }) {
    _state.executions = { ..._state.executions, [campaignId]: data };
  },
  [SET_CAMPAIGN_V2_EXECUTION_MESSAGES](_state, { executionId, data }) {
    _state.executionMessages = {
      ..._state.executionMessages,
      [executionId]: data,
    };
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
